import axios from 'axios';
import Qs from 'qs';
import {
  CreatingPatentFtoInventionTheme,
  ExcludedElementSuggestion,
  KeywordSearchCriteria,
  PatentFtoAnalysis,
  PatentFtoAnalysisExportRow,
  PatentFtoAnalysisPatentResult,
  PatentFtoEventData,
  PatentFtoGetExportDataRequestParam,
  PatentFtoProject,
  PatentIdentifier,
  PatentV2,
  ResultClaimDetail,
  SubjectPatentKey,
} from '@/types/patentFto';
import { handleStreamingResponse } from '@/utils/streaming';

export const getPatentFtoProject = async (id: number) => {
  const { data } = await axios.get<{
    patent_fto_project: PatentFtoProject;
  }>(`/patent_fto/projects/${id}`);
  return data;
};

export const updatePatentFtoProjectTitle = async (
  id: number,
  title: string,
) => {
  const { data } = await axios.patch<{
    patent_fto_project: PatentFtoProject;
  }>(`/patent_fto/projects/${id}`, { title });
  return data.patent_fto_project;
};

export const deletePatentFtoProject = async (id: number) => {
  await axios.delete(`/patent_fto/projects/${id}`);
};

export const getPatentFtoProjectFavorites = async (id: number) => {
  const { data } = await axios.get<{
    is_favorite: boolean;
    can_add_favorite: boolean;
  }>(`/patent_fto/projects/${id}/favorites`);
  return data;
};

export const createPatentFtoProjectFavorites = async (id: number) => {
  const { data } = await axios.post<{ favorited_at: string }>(
    `/patent_fto/projects/${id}/favorites`,
  );
  return data;
};

export const deletePatentFtoProjectFavorites = async (id: number) => {
  await axios.delete<void>(`/patent_fto/projects/${id}/favorites`);
};

export const createPatentFtoAnalysis = async (
  projectId?: number,
  inventionTheme?: RevisedInventionThemeContent,
) => {
  const { data } = await axios.post<{
    project: Pick<PatentFtoProject, 'id' | 'title'>;
    analysis: PatentFtoAnalysis<'invention_theme'>;
  }>('/patent_fto/analyses', {
    project_id: projectId,
    invention_theme: inventionTheme,
  });
  return data;
};

// 調査の更新
export type UpdateAnalysisEvent = {
  event: 'updateAnalysis';
  id: number; // analysis id
  title?: string;
  invention_theme?: {
    theme_elements: string[];
    excluded_elements?: string[];
    extra_rules?: string[];
  };
  subject_patents?: SubjectPatentKey[];
};

// 既存 project へ切替
export type ChangeProjectEvent = {
  event: 'changeProject';
  id: number; // analysis id
  project_id: number;
};

// 新規プロジェクト作成 & そのプロジェクトに紐付けを切り替え
export type CreateProjectEvent = {
  event: 'createProject';
  id: number; // analysis id
  new_project_title: string;
};

export const updatePatentFtoAnalysis = async (
  params: UpdateAnalysisEvent | ChangeProjectEvent | CreateProjectEvent,
) => {
  const { event, ...restParams } = params;
  await axios.patch('/patent_fto/analyses', restParams);
};

type StartPatentFtoAnalysisBaseParams = {
  id: number;
  project_id: number;
  invention_theme?: {
    theme_elements: string[];
    excluded_elements?: string[];
    extra_rules?: string[];
  };
  tracking_data: Pick<PatentFtoEventData, 'page' | 'search_session_id'>;
};

export type SubjectPatentsSelection =
  | {
      subject_mode: 'explicit';
      subject_patents: SubjectPatentKey[];
      // nullはキーワード検索由来でないことを表す（空Hashはキーワード検索由来だが検索条件未指定）
      keyword_search_criteria: KeywordSearchCriteria | null;
    }
  | {
      subject_mode: 'keyword_search';
      exclude_patent_keys: SubjectPatentKey[];
      keyword_search_criteria: KeywordSearchCriteria;
    };

export const startPatentFtoAnalysis = async (
  params:
    | (StartPatentFtoAnalysisBaseParams &
        SubjectPatentsSelection & {
          analysis_start_mode: 'Initial';
        })
    | (StartPatentFtoAnalysisBaseParams & {
        analysis_start_mode: 'Rerun';
      }),
) => {
  const { data } = await axios.post<{
    analysis: PatentFtoAnalysis;
  }>('/patent_fto/analyses/start_patent_fto_analysis', params);
  return data;
};

export const getPatentFtoAnalysisCount = async (projectId?: number) => {
  const params = { project_id: projectId };

  const { data } = await axios.get<{ total_count: number }>(
    `/patent_fto/analyses/total_count`,
    { params },
  );
  return data.total_count;
};

export type SearchPatentsByFileResponse = {
  patents: PatentV2[];
  search_result_rows: Array<Array<string>>;
  failed_count: number;
};

export type SearchPatentsByFileErrorResponse = {
  error_type:
    | 'identifier_type_conflict_in_header_error'
    | 'file_parse_error'
    | 'no_valid_identifier_header_error';
};

export const searchPatentsByFile = async (
  file: File,
  tracking_data: Pick<PatentFtoEventData, 'page' | 'analysis' | 'project'>,
) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append(
    'file_format',
    file.name.toLowerCase().endsWith('.xlsx') ? 'excel' : 'csv',
  );
  formData.append('tracking_data', JSON.stringify(tracking_data));

  const { data } = await axios.post<SearchPatentsByFileResponse>(
    '/patent_fto/search_patents_by_identifiers',
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    },
  );
  return data;
};

export type SearchPatentsByKeywordParams = KeywordSearchCriteria & {
  page: number;
  limit: number;
};

export type SearchPatentsByKeywordResponse = {
  patents: PatentV2[];
  has_more: boolean;
  window_exceeded: boolean;
  total_count: number;
};

export const searchPatentsByKeyword = async (
  params: SearchPatentsByKeywordParams,
) => {
  const { data } = await axios.post<SearchPatentsByKeywordResponse>(
    '/patent_fto/search_patents_by_keyword',
    params,
  );
  return data;
};

export const createPatentFtoProject = async (title: string) => {
  const { data } = await axios.post<{
    patent_fto_project: PatentFtoProject;
  }>('/patent_fto/projects', { title });

  return data.patent_fto_project;
};

export const deletePatentFtoAnalysis = async (id: number) => {
  await axios.delete(`/patent_fto/analyses/${id}`);
};

export const ANALYSIS_PREVIEW_STREAMING_DATA_TYPES = {
  ERROR: 'error', // エラー
  RESULT: 'result', // 生成結果
} as const;

export type CreatePatentFtoAnalysisPreviewResponse =
  | {
      type: (typeof ANALYSIS_PREVIEW_STREAMING_DATA_TYPES)['ERROR'];
    }
  | {
      type: (typeof ANALYSIS_PREVIEW_STREAMING_DATA_TYPES)['RESULT'];
      data: PatentFtoAnalysisPatentResult[];
    };

export async function* executePatentFtoAnalysisPreview(
  params: {
    project_id: number;
    invention_theme: Omit<CreatingPatentFtoInventionTheme, 'id'>;
    subject_patents: SubjectPatentKey[];
  },
  signal?: AbortSignal,
): AsyncGenerator<CreatePatentFtoAnalysisPreviewResponse> {
  const config = {
    url: '/api/v1/patent_fto/analyses/execute_patent_fto_analysis_preview',
    method: 'POST' as const,
    body: JSON.stringify(params),
    acceptedTypes: Object.values(ANALYSIS_PREVIEW_STREAMING_DATA_TYPES),
    signal,
  };
  yield* handleStreamingResponse<CreatePatentFtoAnalysisPreviewResponse>(
    config,
  );
}

export const GENERATE_EXCLUDED_ELEMENT_SUGGESTIONS_STREAMING_DATA_TYPES = {
  ERROR: 'error', // エラー
  RESULT: 'result', // 生成結果
} as const;

export type GenerateExcludedElementSuggestionsResponse =
  | {
      type: (typeof GENERATE_EXCLUDED_ELEMENT_SUGGESTIONS_STREAMING_DATA_TYPES)['ERROR'];
      message: string;
    }
  | {
      type: (typeof GENERATE_EXCLUDED_ELEMENT_SUGGESTIONS_STREAMING_DATA_TYPES)['RESULT'];
      data: {
        results: ExcludedElementSuggestion[];
      };
    };

export async function* generateExcludedElementSuggestions(
  params: {
    analysis_id: number;
  },
  signal?: AbortSignal,
): AsyncGenerator<GenerateExcludedElementSuggestionsResponse> {
  const config = {
    url: '/api/v1/patent_fto/analyses/generate_excluded_elements',
    method: 'POST' as const,
    body: JSON.stringify(params),
    acceptedTypes: Object.values(
      GENERATE_EXCLUDED_ELEMENT_SUGGESTIONS_STREAMING_DATA_TYPES,
    ),
    signal,
  };
  yield* handleStreamingResponse<GenerateExcludedElementSuggestionsResponse>(
    config,
  );
}

export const EXTRACT_INVENTION_THEME_ELEMENTS_STREAMING_DATA_TYPES = {
  ERROR: 'error', // エラー
  RESULT: 'result', // 生成結果
} as const;

export type GenerateInventionThemeElementsResponse =
  | {
      type: (typeof EXTRACT_INVENTION_THEME_ELEMENTS_STREAMING_DATA_TYPES)['ERROR'];
    }
  | {
      type: (typeof EXTRACT_INVENTION_THEME_ELEMENTS_STREAMING_DATA_TYPES)['RESULT'];
      data: { elements: string[] };
    };

export async function* generateInventionThemeElements(
  sourceText: string,
  signal?: AbortSignal,
): AsyncGenerator<GenerateInventionThemeElementsResponse> {
  const config = {
    url: 'api/v1/patent_fto/invention_themes/generate_elements',
    method: 'POST' as const,
    body: JSON.stringify({
      source_text: sourceText,
    }),
    acceptedTypes: Object.values(
      EXTRACT_INVENTION_THEME_ELEMENTS_STREAMING_DATA_TYPES,
    ),
    signal,
  };
  yield* handleStreamingResponse<GenerateInventionThemeElementsResponse>(
    config,
  );
}

export const getExportData = async (
  analysisId: number,
  params: PatentFtoGetExportDataRequestParam,
): Promise<PatentFtoAnalysisExportRow[]> => {
  const { resultIds, ...restParams } = params;
  const { data } = await axios.get(
    `/patent_fto/analyses/${analysisId}/export_analysis_results`,
    {
      params: {
        ...restParams,
        result_ids: resultIds.length > 0 ? resultIds : '',
      },
      paramsSerializer: params => {
        return Qs.stringify(params, {
          strictNullHandling: true,
          arrayFormat: 'brackets',
        });
      },
    },
  );
  return data;
};

// ==== 構成要件判定結果の検証 ====

// リクエストで指定できる想定関連度
export type ExpectedSimilarity =
  | 'match'
  | 'partial_match'
  | 'non_match'
  | 'not_mentioned';

// 判定基準の修正案 diff
export type CriteriaDiffStatus = 'added' | 'removed' | 'unchanged';
export type CriteriaDiffEntry = { value: string; status: CriteriaDiffStatus };
export type InventionThemeDiff = {
  theme_elements: CriteriaDiffEntry[];
  excluded_elements: CriteriaDiffEntry[];
  extra_rules: CriteriaDiffEntry[];
};

export type VerificationStatus = 'aligned' | 'not_aligned' | 'invalid';

// 非同期検証の実行状態。stale な processing はサーバー側で failed として表出される
export type VerificationGenerationStatus =
  | 'processing'
  | 'completed'
  | 'failed';

// 保存済みの検証結果(verify 投入 API および取得系 API のレスポンス)。
// generation_status が completed のときのみ結果系フィールド(status / revised_invention_theme /
// inference_metadata / estimated_detail_item)が入る
export type ClaimResultVerification = {
  id: number;
  claim_result_id: number;
  claim_element_index: number;
  generation_status: VerificationGenerationStatus;
  status: VerificationStatus | null;
  adopted: boolean;
  expected_similarity: ExpectedSimilarity;
  user_reason: string | null;
  revised_invention_theme: InventionThemeDiff | null;
  inference_metadata: {
    inferred_reason: string; // UI上は未使用
    change_summary: string; // UI上は未使用
  } | null;
  estimated_detail_item: ResultClaimDetail | null; // status が invalid の場合も null
  patent_claim_number: number;
  claim_element: string;
  created_at: string;
  updated_at: string;
};

// 非同期検証の投入。202 で processing のレコードを返す。
// 同一要素が処理中の場合サーバーは 409 を返す（呼び出し側でポーリングに合流する）
export const verifyClaimResult = async (params: {
  analysisId: number;
  claimResultId: number;
  claimElementIndex: number;
  expectedSimilarity: ExpectedSimilarity;
  reason?: string;
}): Promise<ClaimResultVerification> => {
  const { data } = await axios.post<ClaimResultVerification>(
    `/patent_fto/analyses/${params.analysisId}/claim_results/${params.claimResultId}/verify`,
    {
      claim_element_index: params.claimElementIndex,
      expected_similarity: params.expectedSimilarity,
      reason: params.reason,
    },
  );
  return data;
};

// ---- 検証結果の取得・採用（デフォルト保存: verify 実行時にサーバーが自動 upsert する） ----

// analysis 全体一覧（検証結果タブ用）の要素
export type ClaimResultVerificationListItem = ClaimResultVerification & {
  patent_identifier: PatentIdentifier | null;
  pct_application_number: string | null;
  pct_publication_number: string | null;
};

export type PatentFtoClaimResultVerificationListResponse = {
  claim_result_verifications: ClaimResultVerificationListItem[];
};

// 構成要件1件の保存済み検証結果（サイドピークの既存結果提示・完了ポーリング用）。
// 未検証は正常系として null が返る。取得失敗（404 等）は「未検証」と区別する必要が
// あるため握り潰さず throw する
export const getClaimResultVerification = async (
  analysisId: number,
  claimResultId: number,
  claimElementIndex: number,
): Promise<ClaimResultVerification | null> => {
  const { data } = await axios.get<{
    claim_result_verification: ClaimResultVerification | null;
  }>(
    `/patent_fto/analyses/${analysisId}/claim_results/${claimResultId}/verification`,
    { params: { claim_element_index: claimElementIndex } },
  );
  return data.claim_result_verification;
};

export const listClaimResultVerifications = async (
  analysisId: number,
  params?: { adopted?: boolean },
): Promise<PatentFtoClaimResultVerificationListResponse> => {
  const { data } =
    await axios.get<PatentFtoClaimResultVerificationListResponse>(
      `/patent_fto/analyses/${analysisId}/claim_result_verifications`,
      { params },
    );
  return data;
};

// adopted: true は status: aligned のレコードのみ許可（それ以外は 400）
export const adoptClaimResultVerification = async (
  analysisId: number,
  verificationId: number,
  adopted: boolean,
): Promise<ClaimResultVerification> => {
  const { data } = await axios.patch<ClaimResultVerification>(
    `/patent_fto/analyses/${analysisId}/claim_result_verifications/${verificationId}`,
    { adopted },
  );
  return data;
};

// ---- 判定基準（revised invention theme） ----

// 確定値3配列（PUT の body / 生成結果の確定値部分）
export type RevisedInventionThemeContent = {
  theme_elements: string[];
  excluded_elements: string[];
  extra_rules: string[];
};

export type PatentFtoRevisedInventionTheme = RevisedInventionThemeContent & {
  id: number;
  created_at: string;
  updated_at: string;
};

// 生成 SSE の result data: 確定値3配列 + 差分プレビュー用 diff
export type RevisedInventionThemeGenerationResult =
  RevisedInventionThemeContent & {
    diff: InventionThemeDiff;
  };

export type GenerateRevisedInventionThemeResponse =
  | { type: 'result'; data: RevisedInventionThemeGenerationResult }
  | { type: 'error'; message: string };

// 生成 SSE の result data の実際のワイヤー形。
// バックエンド(PatentFtoInventionThemeRevisionMergeStreamer)は確定値3配列を
// revised_invention_theme キー配下にネストして返す。フロント側の契約
// (RevisedInventionThemeGenerationResult)はフラットな形のため、ここで詰め替える。
// なお確定値3配列は PatentFtoInventionThemeRevisionMerger 内で Array() によって
// 常に配列化されるため（DB 由来の null 正規化とは異なり）null になり得ない。
type GenerateRevisedInventionThemeWireResponse =
  | {
      type: 'result';
      data: {
        revised_invention_theme: RevisedInventionThemeContent;
        diff: InventionThemeDiff;
      };
    }
  | { type: 'error'; message: string };

// バックエンドは excluded_elements / extra_rules が空のとき null を返す
// （PatentFtoInventionThemeContent#normalize_string_array の allow_empty: false 仕様）。
// フロント側では「確定値3配列は常に string[]」という契約を保つため、ここで正規化する。
const normalizeRevisedInventionTheme = (
  theme: PatentFtoRevisedInventionTheme,
): PatentFtoRevisedInventionTheme => ({
  ...theme,
  theme_elements: theme.theme_elements ?? [],
  excluded_elements: theme.excluded_elements ?? [],
  extra_rules: theme.extra_rules ?? [],
});

export const getRevisedInventionTheme = async (
  analysisId: number,
): Promise<PatentFtoRevisedInventionTheme | null> => {
  try {
    const { data } = await axios.get<PatentFtoRevisedInventionTheme>(
      `/patent_fto/analyses/${analysisId}/revised_invention_theme`,
    );
    return normalizeRevisedInventionTheme(data);
  } catch (error) {
    // 未作成は 404 で返る。フロントでは「空状態」として扱う
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

export const putRevisedInventionTheme = async (
  analysisId: number,
  content: RevisedInventionThemeContent,
): Promise<PatentFtoRevisedInventionTheme> => {
  const { data } = await axios.put<PatentFtoRevisedInventionTheme>(
    `/patent_fto/analyses/${analysisId}/revised_invention_theme`,
    content,
  );
  return normalizeRevisedInventionTheme(data);
};

export const postRevisedInventionTheme = async (
  analysisId: number,
  content: RevisedInventionThemeContent,
): Promise<PatentFtoRevisedInventionTheme> => {
  const { data } = await axios.post<PatentFtoRevisedInventionTheme>(
    `/patent_fto/analyses/${analysisId}/revised_invention_theme`,
    content,
  );
  return normalizeRevisedInventionTheme(data);
};

export const GENERATE_REVISED_INVENTION_THEME_STREAMING_DATA_TYPES = {
  ERROR: 'error',
  RESULT: 'result',
} as const;

// 判定基準の生成（SSE）。verification_ids は採用済み（adopted）の検証結果のみ許可（未採用が混じると 400）。
// 生成結果は永続化されない（ユーザーが差分プレビューで確定したら postRevisedInventionTheme で保存する）
export async function* generateRevisedInventionTheme(
  params: { analysisId: number; verificationIds: number[] },
  signal?: AbortSignal,
): AsyncGenerator<GenerateRevisedInventionThemeResponse> {
  const config = {
    url: `/api/v1/patent_fto/analyses/${params.analysisId}/revised_invention_theme/generate`,
    method: 'POST' as const,
    body: JSON.stringify({ verification_ids: params.verificationIds }),
    acceptedTypes: Object.values(
      GENERATE_REVISED_INVENTION_THEME_STREAMING_DATA_TYPES,
    ),
    signal,
  };
  for await (const chunk of handleStreamingResponse<GenerateRevisedInventionThemeWireResponse>(
    config,
  )) {
    if (chunk.type === 'error') {
      yield chunk;
      continue;
    }
    const { revised_invention_theme, diff } = chunk.data;
    yield {
      type: 'result',
      data: { ...revised_invention_theme, diff },
    };
  }
}
